/**
 * Pure ordering helpers for the CRF Builder's form list.
 *
 * The `forms` array is kept in load/creation order; the displayed order is a
 * derived view chosen by the user (A–Z, by form code, by category, as-created,
 * or a custom drag order). Kept React-free so it's unit-testable.
 */
export type FormSortMode = 'created' | 'az' | 'code' | 'category' | 'custom';

const SORT_MODES: readonly FormSortMode[] = ['created', 'az', 'code', 'category', 'custom'];

/** True when `x` is one of the sort options - used to accept a saved choice. */
export function isFormSortMode(x: unknown): x is FormSortMode {
  return typeof x === 'string' && (SORT_MODES as readonly string[]).includes(x);
}

export interface SortableForm {
  id: string;
  name: string;
  category?: string;
}

const byName = (a: SortableForm, b: SortableForm) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

/** Return a new array of forms in the requested display order. Array#sort is
 *  stable, so ties (and forms missing from a custom order) keep their original
 *  relative order. */
export function sortForms<T extends SortableForm>(
  forms: T[],
  mode: FormSortMode,
  customOrder: string[],
  // The code shown on each form's badge. The page passes the very function the
  // badge renders, so 'code' order always matches what is on screen.
  opts: { codeOf?: (form: T) => string } = {},
): T[] {
  switch (mode) {
    case 'az':
      return [...forms].sort(byName);
    case 'code': {
      // Case-insensitive, numbers compared as numbers (VS2 before VS10), ties
      // by name, and forms with no code last, by name.
      const codeOf = (f: T) => (opts.codeOf?.(f) || '').trim();
      return [...forms].sort((a, b) => {
        const ca = codeOf(a);
        const cb = codeOf(b);
        if (!ca || !cb) return ca ? -1 : cb ? 1 : byName(a, b);
        const c = ca.localeCompare(cb, undefined, { sensitivity: 'base', numeric: true });
        return c !== 0 ? c : byName(a, b);
      });
    }
    case 'category':
      return [...forms].sort((a, b) => {
        const c = (a.category || '').localeCompare(b.category || '', undefined, { sensitivity: 'base' });
        return c !== 0 ? c : byName(a, b);
      });
    case 'custom': {
      const pos = new Map(customOrder.map((id, i) => [id, i] as const));
      return [...forms].sort((a, b) => {
        const pa = pos.has(a.id) ? (pos.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
        const pb = pos.has(b.id) ? (pos.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
        return pa - pb;
      });
    }
    case 'created':
    default:
      return forms;
  }
}

/** Move `draggedId` so it sits immediately before `beforeId` (null = end) in an
 *  ordered list of ids. Used to update the custom drag order. */
export function moveInOrder(order: string[], draggedId: string, beforeId: string | null): string[] {
  if (draggedId === beforeId) return order;
  const without = order.filter((id) => id !== draggedId);
  if (beforeId == null) return [...without, draggedId];
  const i = without.indexOf(beforeId);
  if (i < 0) return [...without, draggedId];
  return [...without.slice(0, i), draggedId, ...without.slice(i)];
}
